import React, { useContext } from 'react';
import { useTheme } from '@emotion/react';
import { AgendaContext } from '../../../contexts/AgendaContext';
// Components
import HorizontalDivider from '../../common/HorizontalDivider';
// Icons
import iconLocation from '../../../assets/icons/icon_location.svg';
import iconChevronDown from '../../../assets/icons/icon_chevron-down.svg';
import iconCircleInformation from '../../../assets/icons/icon_circle-information.svg';
// MuiElements
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Typography
} from '@mui/material';
// Hooks & Constants
import { toCamelCaseAllWords, toCamelCaseFirstWord } from '../../../utils/Hooks/textFormatted';
import { steps } from '../../../utils/Constants/Steps';
// DayJS
import dayjs from 'dayjs';
import { nameFormatted } from '../../../utils/Hooks/nameFormatted';

function PantallaVerificacionDatos({ setActiveStep, setStepHandler }) {
  const { solicitudProps, filialSeleccionada, consultoriosContrato } = useContext(AgendaContext);

  const {
    direccionExterno,
    direccionOsde,
    proveedor,
    especializacion,
    fechaPublicacion,
    datosContacto
  } = solicitudProps;

  const direccionExternoArray = direccionExterno.split(',');
  const direccionExternoFormateada = [...new Set(direccionExternoArray)].join(', ');

  const direccionOsdeArray = direccionOsde.split(',');
  const direccionOsdeFormateada = [...new Set(direccionOsdeArray)].join(', ');

  const phoneNumbers = datosContacto?.filter((dato) => dato.system === 'phone');
  const emailsAddresses = datosContacto?.filter((dato) => dato.system === 'email');

  const theme = useTheme();

  const handleNavigateEdit = (id) => {
    setActiveStep(id);
    if (id === 1) {
      setStepHandler('goStayAndReturn');
    } else {
      setStepHandler('goAndReturn');
    }
  };

  return (
    <>
      <Box>
        <Typography variant="h2">Verificación de datos</Typography>
        <Typography variant="bodyM">
          Revisá que los datos ingresados sean correctos. Una vez enviada, no podrás editar la
          solicitud.
        </Typography>

        <Box
          sx={{
            width: '80%',
            maxWidth: '608px',
            backgroundColor: theme.palette.backgroundSecondary.main,
            padding: '1.5rem',
            borderRadius: '16px',
            marginTop: '2rem'
          }}>
          <Typography variant="h3">{nameFormatted(consultoriosContrato.nombre)}</Typography>

          <Box display="flex" alignItems="center" gap=".25rem">
            <Box
              component="img"
              sx={{ width: '16px', height: '16px' }}
              alt="img not found"
              src={iconLocation}
            />
            <Typography variant="lists">
              Filial {toCamelCaseAllWords(filialSeleccionada)}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ marginTop: '1rem', width: '80%', maxWidth: '608px' }}>
          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{ '&:before': { height: '0px' } }}>
            <AccordionSummary
              expandIcon={
                <Box
                  component="img"
                  sx={{ width: '24px', height: '24px' }}
                  alt="img not found"
                  src={iconChevronDown}
                />
              }
              sx={{ paddingX: 0 }}>
              <Typography variant="h4" component="p">
                Consultorios a conectar
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: 0 }}>
              <Box display={'flex'} flexDirection="column" gap={'1rem'}>
                <Box>
                  <Typography color={theme.palette.gray.main} variant="bodyS" component="h3">
                    Consultorio publicado en{' '}
                    {proveedor.charAt(0) + proveedor.slice(1).toLowerCase()}
                  </Typography>
                  <Typography variant="h5" component="p">
                    {direccionExternoFormateada}
                  </Typography>
                </Box>
                <Box>
                  <Typography color={theme.palette.gray.main} variant="bodyS" component="h3">
                    Consultorio publicado en OSDE
                  </Typography>
                  <Typography variant="h5" component="p">
                    {direccionOsdeFormateada}
                  </Typography>
                </Box>
                <Box display="flex" justifyContent="end">
                  <Button
                    variant="text"
                    sx={{ textDecoration: 'underline' }}
                    onClick={() => handleNavigateEdit(steps[1].id)}>
                    Editar datos
                  </Button>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
          <HorizontalDivider />

          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{ marginTop: '1rem', '&:before': { height: '0px' } }}>
            <AccordionSummary
              expandIcon={
                <Box
                  component="img"
                  sx={{ width: '24px', height: '24px' }}
                  alt="img not found"
                  src={iconChevronDown}
                />
              }
              sx={{ paddingX: 0 }}>
              <Typography variant="h4" component="p">
                Especialidades seleccionadas
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: 0 }}>
              <Typography variant="h5" component="p">
                {especializacion
                  .filter((especialidad) => especialidad.solicitada)
                  .map((especialidad, index, array) => {
                    if (index === array.length - 1) {
                      return ` ${toCamelCaseFirstWord(especialidad.descripcion)}`;
                    }
                    return ` ${toCamelCaseFirstWord(especialidad.descripcion)} |`;
                  })}
              </Typography>
              <Box display="flex" justifyContent="end" marginTop="1rem">
                <Button
                  variant="text"
                  sx={{ textDecoration: 'underline' }}
                  onClick={() => handleNavigateEdit(steps[2].id)}>
                  Editar datos
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
          <HorizontalDivider />

          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{ marginTop: '1rem', '&:before': { height: '0px' } }}>
            <AccordionSummary
              expandIcon={
                <Box
                  component="img"
                  sx={{ width: '24px', height: '24px' }}
                  alt="img not found"
                  src={iconChevronDown}
                />
              }
              sx={{ paddingX: 0 }}>
              <Typography variant="h4" component="p">
                Fecha de publicación solicitada
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: 0 }}>
              <Typography variant="h5" component="p">
                {dayjs(fechaPublicacion * 1000).format('DD/MM/YYYY')}
              </Typography>

              <Box sx={{ display: 'flex', gap: '.5rem', marginTop: '.5rem' }}>
                <Box
                  component="img"
                  sx={{ width: '16px', height: '16px' }}
                  alt="img not found"
                  src={iconCircleInformation}
                />
                <Typography variant="bodyS">
                  Esta fecha es de referencia. Una vez aprobada la solicitud te confirmaremos la
                  fecha de publicación definitiva.
                </Typography>
              </Box>

              <Box display="flex" justifyContent="end" marginTop="1rem">
                <Button
                  variant="text"
                  sx={{ textDecoration: 'underline' }}
                  onClick={() => handleNavigateEdit(steps[3].id)}>
                  Editar datos
                </Button>
              </Box>
            </AccordionDetails>
          </Accordion>
          <HorizontalDivider />

          <Accordion
            defaultExpanded
            disableGutters
            elevation={0}
            sx={{ marginTop: '1rem', '&:before': { height: '0px' } }}>
            <AccordionSummary
              expandIcon={
                <Box
                  component="img"
                  sx={{ width: '24px', height: '24px' }}
                  alt="img not found"
                  src={iconChevronDown}
                />
              }
              sx={{ paddingX: 0 }}>
              <Typography variant="h4" component="p">
                Datos de contacto
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ paddingX: 0 }}>
              <Box display="flex" flexDirection="column" gap={'1rem'}>
                <Box>
                  <Typography color={theme.palette.gray.main} variant="bodyS" component="h3">
                    Teléfonos
                  </Typography>
                  <Typography variant="h5" component="p">
                    {phoneNumbers.map((numero, index) => {
                      if (index === phoneNumbers.length - 1) {
                        return ` ${numero.value}`;
                      }
                      return ` ${numero.value} |`;
                    })}
                  </Typography>
                </Box>
                <Box>
                  <Typography color={theme.palette.gray.main} variant="bodyS" component="h3">
                    Correos electrónicos
                  </Typography>
                  <Typography variant="h5" component="p">
                    {emailsAddresses.map((numero, index) => {
                      if (index === emailsAddresses.length - 1) {
                        return ` ${numero.value}`;
                      }
                      return ` ${numero.value} |`;
                    })}
                  </Typography>
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>
    </>
  );
}

export default PantallaVerificacionDatos;
